import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { apiService } from "service/ApiService";
import { notify } from "service/NotificationService";

// Buttons
import CreateButton from "components/Buttons/CreateButton";
import EditButton from "components/Buttons/EditButton";
import ExitButton from "components/Buttons/ExitButton";

// Selectors
import DaysSelector from "components/Selectors/DaysSelector";
import HoursSelector from "components/Selectors/HoursSelector";
import ProductsSelector from "components/Selectors/ProductsSelector";

// Forms
import TargetForm from "components/Forms/TargetForm";

export default function CreateCompanyPage() {
  const backRoute = "/admin/companies";
  const history = useHistory();
  const { id } = useParams();
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyVatNumber, setCompanyVatNumber] = useState("");
  const [companyBusiness, setCompanyBusiness] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyBranch, setCompanyBranch] = useState(null);
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyDoy, setCompanyDoy] = useState("");
  const [providerUsername, setProviderUsername] = useState("");
  const [providerPassword, setProviderPassword] = useState("");
  const [workingDays, setWorkingDays] = useState([]);
  const [workingHours, setWorkingHours] = useState("");
  const [sellingProductIds, setSellingProductIds] = useState([]);
  const [invoiceSeries, setInvoiceSeries] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isDisabled, setIsDisabled] = useState(false);

  const handleError = (error) => notify("error", error);
  const handleCreate = (create) => notify("create", create);
  const handleUpdate = (update) => notify("update", update);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (!id) return;
    apiService
      .get(`api/companies/${id}`)
      .then((company) => {
        fillFields(company);
      })
      .catch((error) => {
        handleError(error.message);
        history.push(backRoute);
      });
  }, []);

  const handleProductsSelected = (ids) => {
    setSellingProductIds(ids);
  };

  const handleDaysChange = (selectedDays) => {
    setWorkingDays(selectedDays);
  };

  const save = () => {
    setIsDisabled(true);
    const company = {
      name: companyName ? companyName : undefined,
      vat_number: companyVatNumber ? companyVatNumber : undefined,
      bussiness: companyBusiness ? companyBusiness : undefined,
      address: companyAddress ? companyAddress : undefined,
      branch: companyBranch ? companyBranch : undefined,
      phone: companyPhone ? companyPhone : undefined,
      doy: companyDoy ? companyDoy : undefined,
      provider_username: providerUsername ? providerUsername : undefined,
      provider_password: providerPassword ? providerPassword : undefined,
      working_days: workingDays ? workingDays : [],
      working_hours: workingHours ? workingHours : undefined,
      selling_product_ids: sellingProductIds ? sellingProductIds : null,
      invoice_series: invoiceSeries ? invoiceSeries : undefined,
      invoice_number: invoiceNumber ? invoiceNumber : undefined,
    };

    if (id) {
      apiService
        .putJson(`api/companies/${id}`, company)
        .then((resp) => {
          handleUpdate("The company saved successfully");
          setIsDisabled(false);
          window.location.reload();
        })
        .catch((error) => {
          setErrorMessage(error.message);
          handleError(error.message);
          setIsDisabled(false);
        });
    } else {
      apiService
        .postJson("api/companies", company)
        .then((resp) => {
          handleCreate("The company created successfully");
          setIsDisabled(false);
          window.location.reload();
        })
        .catch((error) => {
          setErrorMessage(error.message);
          handleError(error.message);
          setIsDisabled(false);
        });
    }
  };

  const fillFields = (company) => {
    setCompanyId(company._id);
    setCompanyName(company.name);
    setCompanyVatNumber(company.vat_number);
    setCompanyBusiness(company.bussiness);
    setCompanyAddress(company.address);
    setCompanyBranch(company.branch);
    setCompanyPhone(company.phone);
    setCompanyDoy(company.doy);
    setProviderUsername(company.provider_username);
    setProviderPassword(company.provider_password);
    setWorkingDays(company.working_days);
    setWorkingHours(company.working_hours);
    setInvoiceSeries(company.invoice_series);
    setInvoiceNumber(company.invoice_number);
    setSellingProductIds(company.selling_product_ids);
  };

  return (
    <>
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
        <div className="rounded-t bg-white px-6 py-6">
          <div className="text-center flex justify-between">
            <h6 className="text-blueGray-700 text-xl font-bold">{id ? "Edit Company" : "Create Company"}</h6>
            <ExitButton handler={() => history.push(backRoute)} />
          </div>
        </div>
        <div className="flex flex-wrap px-4 lg:px-10 py-10 pt-0">
          <div className="w-full sm:w-8/12">
            <form>
              <h6 className="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Company Basics</h6>
              <div className="flex flex-wrap">
                <div className="w-1/2 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Company Name
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Company Name"
                      required={true}
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-1/2 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      VAT Number
                    </label>
                    <input
                      type="email"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="VAT Number"
                      required={true}
                      value={companyVatNumber}
                      onChange={(e) => {
                        setCompanyVatNumber(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <hr className="mt-6 border-b-1 border-blueGray-300" />
              <h6 className="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Company Details</h6>
              <div className="flex flex-wrap">
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Business
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Business"
                      required={true}
                      value={companyBusiness}
                      onChange={(e) => {
                        setCompanyBusiness(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Address
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Address"
                      required={true}
                      value={companyAddress}
                      onChange={(e) => {
                        setCompanyAddress(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Branch
                    </label>
                    <input
                      type="number"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Branch"
                      required={true}
                      value={companyBranch}
                      onChange={(e) => {
                        setCompanyBranch(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap">
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Phone Number"
                      required={true}
                      value={companyPhone}
                      onChange={(e) => {
                        setCompanyPhone(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Tax Office
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Tax Office"
                      required={true}
                      value={companyDoy}
                      onChange={(e) => {
                        setCompanyDoy(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Provider Username
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Provider Username"
                      required={true}
                      value={providerUsername}
                      onChange={(e) => {
                        setProviderUsername(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap">
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Provider Password
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Provider Password"
                      required={true}
                      value={providerPassword}
                      onChange={(e) => {
                        setProviderPassword(e.target.value);
                      }}
                    />
                    <button type="button" className="show-hide-ps" onClick={toggleShowPassword}>
                      {showPassword ? <i className="fa fa-eye-slash"></i> : <i className="fa fa-eye"></i>}
                    </button>
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Working Days
                    </label>
                    <DaysSelector selectedDayNums={workingDays} onDaysChange={handleDaysChange} />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Working Hours
                    </label>
                    <HoursSelector value={workingHours} setValue={setWorkingHours} />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap">
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Selling products
                    </label>
                    <ProductsSelector onChange={handleProductsSelected} selectedIds={sellingProductIds} />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Invoice Series
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Invoice Series"
                      required={true}
                      value={invoiceSeries}
                      onChange={(e) => {
                        setInvoiceSeries(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full px-4 md:w-4/12">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2" htmlFor="grid-password">
                      Invoice Number
                    </label>
                    <input
                      type="number"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Invoice Number"
                      required={true}
                      value={invoiceNumber}
                      onChange={(e) => {
                        setInvoiceNumber(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <hr className="mt-6 border-b-1 border-blueGray-300" />
              <div className="pt-4">
                {id ? <EditButton handler={save} disabled={isDisabled} /> : <CreateButton handler={save} disabled={isDisabled} />}
              </div>
              {errorMessage ? (
                <>
                  <p style={{ color: "crimson" }}>
                    <i className="fa fa-exclamation-circle"></i> {errorMessage}
                  </p>
                </>
              ) : null}
            </form>
          </div>
          <div className="sm:w-4/12 px-4">
           <TargetForm companyId={id} />
          </div>
        </div>
      </div>
    </>
  );
}
